/**
 * Hell, dunkel oder wie das Gerät — die Wahl des Erscheinungsbilds.
 *
 * Bis zum 18.08.2026 war der Leitstand dunkel und fest. Andreas wollte, dass
 * jede Person das für sich entscheiden kann; die Farbwerte dafür stehen in
 * `styles/index.css`.
 *
 * Wo die Wahl gemerkt wird, hängt vom Ort ab:
 *
 *   Leitstand   — an der Person. Frank, Richard und Doreen arbeiten teils am
 *                 selben Rechner; die Wahl soll der Person folgen, nicht dem
 *                 Gerät.
 *   Stationen   — am Gerät. Dort meldet sich die Schicht reihum am selben
 *                 Rechner an; ein Wechsel des Bildes bei jedem Chip wäre
 *                 Unruhe statt Komfort.
 *
 * Die Wahl liegt zusätzlich im Browser, damit das Bild beim Laden sofort
 * stimmt und nicht erst nach der ersten Antwort des Servers umspringt.
 */

export type Farbschema = 'hell' | 'dunkel' | 'system';

const SPEICHER_SCHLUESSEL = 'washops.farbschema';

/**
 * Der Browser-Speicher ist nicht überall da: in der Testumgebung fehlt er, und
 * ein Browser im strengen Datenschutzmodus verweigert ihn. Beides darf die
 * Oberfläche nicht zum Absturz bringen — ohne Speicher gilt schlicht „wie das
 * Gerät".
 */
function speicher(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function'
      ? localStorage
      : null;
  } catch {
    return null;
  }
}

export function gespeichertesSchema(): Farbschema {
  const wert = speicher()?.getItem(SPEICHER_SCHLUESSEL);
  return wert === 'hell' || wert === 'dunkel' ? wert : 'system';
}

/**
 * Trägt die Wahl an das Wurzelelement. „system" setzt nichts — dann greift
 * die Regel in der Design-Datei, die auf die Geräteeinstellung hört.
 */
export function wendeSchemaAn(schema: Farbschema): void {
  if (typeof document === 'undefined') return;
  const wurzel = document.documentElement;
  if (schema === 'system') {
    wurzel.removeAttribute('data-schema');
  } else {
    wurzel.setAttribute('data-schema', schema);
  }
}

export function merkeSchema(schema: Farbschema): void {
  const s = speicher();
  if (s) {
    if (schema === 'system') s.removeItem(SPEICHER_SCHLUESSEL);
    else s.setItem(SPEICHER_SCHLUESSEL, schema);
  }
  wendeSchemaAn(schema);
}

/** Was gerade tatsächlich zu sehen ist — für die Anzeige im Schalter. */
export function tatsaechlichesSchema(schema: Farbschema): 'hell' | 'dunkel' {
  if (schema !== 'system') return schema;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dunkel';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'hell' : 'dunkel';
}

/**
 * Beim Start anwenden. Wird früh aufgerufen, damit kein kurzes Aufblitzen des
 * falschen Bildes entsteht.
 */
export function schemaBeimStartAnwenden(): void {
  wendeSchemaAn(gespeichertesSchema());
}
